import { useDeviceType } from '@cian/ui-kit/responsive/hooks/useDeviceType';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton } from '@cian/frontend-reviews-components';
import { IconSocialDislikeOn16, IconSocialLikeOn16 } from '@cian/ui-kit-design-tokens/icons';

import { setAnswersPage, setAnswersVisibility, setReviewReaction } from '../../../actions/reviews';
import { ButtonLink } from '../../../components/ButtonLink';
import { Footer } from '../../../components/Footer';
import { getReviewById } from '../../../selectors/reviews';
import { EStatus } from '../../../types/likes';
import { ReviewPopupContainer } from '../../ReviewPopup';
import { requestAuthentication } from '../../../actions/authentication';
import { getIsAuthenticated } from '../../../selectors/user';
import { loadAnswers } from '../../../actions/answers/loadAnswers';
import { NewAnswerButtonContainer } from '../../NewAnswerButton';
import { trackOpenThread } from '../../QuestionItem/tracking';

interface IFooterContainerProps {
  reviewId: string;
  inPopup?: boolean;
}

export function FooterContainer({ reviewId, inPopup }: IFooterContainerProps) {
  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const isDesktop = deviceType === 'desktop';
  const [reviewPopupId, setReviewPopupId] = React.useState<string | null>(null);
  const { like, answersCount, areAnswersOpened, hasAnswers } = useSelector(getReviewById(reviewId));
  const { answers } = useSelector(getReviewById(reviewId));

  const onAnswerVisibilityClick = React.useCallback(() => {
    if (!areAnswersOpened || !isDesktop) {
      const page = {
        siteType: deviceType,
        offerType: 'JK',
        pageType: 'ReviewsJK',
        extra: {
          review_id: reviewId,
        },
      };

      // Обернуто в setTimeout для улучшения INP
      setTimeout(() => trackOpenThread(page), 0);
    }

    if (isDesktop) {
      dispatch(setAnswersVisibility({ reviewId, state: !areAnswersOpened }));
      if (!areAnswersOpened && !answers.length) {
        dispatch(loadAnswers({ reviewId, page: 0 }));
      }
    } else {
      setReviewPopupId(reviewId);
    }
  }, [dispatch, areAnswersOpened, reviewId, isDesktop]);

  const onReviewPopupClose = React.useCallback(() => {
    setReviewPopupId(null);
    dispatch(setAnswersPage({ reviewId, page: 0 }));
  }, [dispatch, reviewId]);

  const setReviewLike = React.useCallback(() => setReviewReaction({ reviewId, type: EStatus.Liked }), []);
  const setReviewDisliked = React.useCallback(() => setReviewReaction({ reviewId, type: EStatus.Disliked }), []);

  const onLikeClick = React.useCallback(() => {
    if (isAuthenticated) {
      dispatch(setReviewLike());
    } else {
      dispatch(requestAuthentication({ onSuccess: setReviewLike }));
    }
  }, [dispatch, isAuthenticated, setReviewLike, requestAuthentication]);

  const onDislikedClick = React.useCallback(() => {
    if (isAuthenticated) {
      dispatch(setReviewDisliked());
    } else {
      dispatch(requestAuthentication({ onSuccess: setReviewDisliked }));
    }
  }, [dispatch, isAuthenticated, setReviewDisliked, requestAuthentication]);

  return (
    <>
      <Footer
        like={
          <IconButton
            Icon={IconSocialLikeOn16}
            active={like.status === EStatus.Liked}
            onClick={onLikeClick}
            label={like.likesCount}
          />
        }
        dislike={
          <IconButton
            Icon={IconSocialDislikeOn16}
            active={like.status === EStatus.Disliked}
            onClick={onDislikedClick}
            label={like.dislikesCount}
          />
        }
        answer={<NewAnswerButtonContainer type="review" targetId={reviewId} />}
        answerVisibility={
          !inPopup &&
          hasAnswers && (
            <ButtonLink onClick={onAnswerVisibilityClick} testId="AnswerVisibilityControl">
              {isDesktop && areAnswersOpened ? 'Скрыть ответы' : answersCount}
            </ButtonLink>
          )
        }
      />
      {reviewPopupId !== null && <ReviewPopupContainer reviewId={reviewPopupId} onClose={onReviewPopupClose} />}
    </>
  );
}
